import { registerCompetitors } from '@/services/competitorShirts';

async function refactorPreviewV2(data, sport, category, tournament, producer, onlyManager, source) {

    // Preserve headerSign only if present
    const headerSign = data.headerSign ?? null;

    // 1. Estrai tutti gli eventi spianandoli in un unico array
    let allEvents = [];
    if (Array.isArray(data.events)) {
        data.events.forEach(group => {
            if (group.events) allEvents.push(...group.events);
        });
    } else if (data.events && typeof data.events === 'object') {
        Object.values(data.events).forEach(group => {
            if (group.events) allEvents.push(...group.events);
        });
    }

    // 2. Riordina e raggruppa in base al fuso orario del cliente
    const dateMap = new Map();
    allEvents.sort((a, b) => new Date(a.scheduled) - new Date(b.scheduled)).forEach(ev => {
        // Usiamo en-CA per avere sempre YYYY-MM-DD sulla timezone locale
        const dateKey = ev.scheduled ? new Date(ev.scheduled).toLocaleDateString('en-CA') : 'unknown';

        if (!dateMap.has(dateKey)) {
            dateMap.set(dateKey, { scheduled: dateKey, events: [] });
        }
        dateMap.get(dateKey).events.push(ev);
    });

    // 3. Ricostruisci l'oggetto ordinato
    const eventsObj = {};
    for (const [key, group] of [...dateMap.entries()].sort(([a], [b]) => a.localeCompare(b))) {
        eventsObj[key] = group;
    }

    return {
        sport: { ...sport },
        category: { ...category },
        tournament: { ...tournament },
        events: eventsObj,
        source: source,
        type: 'prematch',
        headerSign: headerSign
    };
}


async function refactorEventDetailV3(eventData) {

    // Preserve headerSign only if present
    const headerSign = eventData.headerSign ?? null;

    // Competitors (with their shirt colours, used by the MatchTracker) may
    // come at the response root: keep them on the event too.
    if (eventData.competitors && eventData.event && !eventData.event.competitors) {
        eventData.event.competitors = eventData.competitors;
    }
    registerCompetitors(eventData.event?.competitors);

    return {
        sport: {
            id: eventData.event.sportId,
            name: eventData.event.sportName
        },
        category: {
            id: eventData.event.categoryId,
            name: eventData.event.categoryName,
        },
        tournament: {
            id: eventData.event.tournamentId,
            name: eventData.event.tournamentName,
        },
        event: eventData.event,
        source: eventData.event.id,
        type: 'prematch',
        headerSign: headerSign,
        groupMarkets: eventData.marketsGroups
    };
}

//todo reause cose is almost equal to  refactorEventDetailV3
async function refactorEventLiveDetailV3(eventData) {

    // Preserve headerSign only if present
    const headerSign = eventData.headerSign ?? null;
    registerCompetitors(eventData.event?.competitors || eventData.competitors);

    return {
        sport: {
            id: eventData.event.sportId,
            name: eventData.event.sportName
        },
        category: {
            id: eventData.event.categoryId,
            name: eventData.event.categoryName,
        },
        tournament: {
            id: eventData.event.tournamentId,
            name: eventData.event.tournamentName,
        },
        event: eventData.event,
        source: eventData.event.id,
        type: 'live',
        headerSign: headerSign,
        groupMarkets: eventData.marketsGroups
    };
}


async function refactorEventAntepostDetailV3(eventData) {

    // Ensure associated events are ordered chronologically
    const siblings = eventData.event.associatedEvents || [];
    siblings.sort((a, b) => new Date(a.scheduledDate) - new Date(b.scheduledDate));

    return {
        sport: {
            id: eventData.event.sport.id,
            name: eventData.event.sport.name
        },
        category: {
            id: eventData.event.category.id,
            name: eventData.event.category.name
        },
        tournament: {
            id: eventData.event.tournament.id,
            name: eventData.event.tournament.name
        },
        event: eventData.event,
        source: eventData.event.id,
        type: 'antepost',

        // Expose associated events to the frontend
        associatedEvents: siblings,

        raceTitle: await getRaceTitle(eventData.event.FixtureExtraData),
        raceNumber: await getRaceNumber(eventData.event.FixtureExtraData)
    };
}


export const refactorEventsGroupedByDateV1 = (apiResult) => {

    if (!apiResult || !apiResult.events) return [];

    // 1. FIX ORDINAMENTO
    // Cloniamo e ordiniamo l'array grezzo PRIMA di raggrupparlo.
    // Ordine: Data (Crescente) -> ID (Crescente come tie-breaker)
    const events = [...apiResult.events].sort((a, b) => {
        const dateA = new Date(a.scheduled).getTime();
        const dateB = new Date(b.scheduled).getTime();

        // Prima controlla la data
        if (dateA !== dateB) return dateA - dateB;

        // Se la data è identica, usa l'ID per tenere l'ordine stabile
        return String(a.id).localeCompare(String(b.id));
    });

    const headerSign = apiResult.headerSign || [];

    // 2. RAGGRUPPAMENTO PER DATA LOCALE
    // Output structure: { "2026-02-18": { events: [...] }, ... }
    const eventsByDate = events.reduce((acc, event) => {

        // FIX DATA ERRATA:
        // Il vecchio codice usava .split('T')[0] che prendeva la data UTC.
        // Se una partita è alle 23:30 UTC, per l'Italia è il giorno dopo.
        // Usiamo toLocaleDateString('en-CA') che restituisce YYYY-MM-DD basandosi sul fuso orario del browser.

        const dateObj = new Date(event.scheduled);
        const dateKey = dateObj.toLocaleDateString('en-CA');

        if (!acc[dateKey]) {
            acc[dateKey] = { events: [] };
        }

        acc[dateKey].events.push(event);
        return acc;

    }, {});

    // 3. COSTRUZIONE OGGETTO VIRTUALE
    const bestPlayedObject = {
        tournament: {
            id: 'best_played_virtual',
            name: 'Most Played Events'
        },
        category: {
            id: 'best_cat',
            name: 'Highlights'
        },
        sport: {
            id: apiResult.sportId || events[0]?.sportId || '6046',
            name: apiResult.sportName || events[0]?.sportName || 'Football'
        },
        type: 'bestplayed', // Custom type for conditional UI logic
        events: eventsByDate,
        headerSign: headerSign,
        source: 'bestplayed_api'
    };

    // La vista si aspetta un array
    return [bestPlayedObject];
};


const getRaceTitle = async (fixtureExtraData) => {

    // Extract "RaceTitle" entry from FixtureExtraData
    return fixtureExtraData.find(el => el.Name === "RaceTitle");
};


const getRaceNumber = async (fixtureExtraData) => {

    // Extract "RaceNumber" entry from FixtureExtraData
    return fixtureExtraData.find(el => el.Name === "RaceNumber");
};


export default {
    refactorEventDetailV3,
    refactorEventsGroupedByDateV1,
    refactorEventAntepostDetailV3,
    refactorPreviewV2,
    refactorEventLiveDetailV3
};
